import { logger } from '@tock/logging';

/**
 * An array whose elements expire after a set duration.
 *
 * Forked to add an expiration callback from:
 * https://github.com/sfrangulov/array-expire/blob/main/src/index.ts
 */
export default class ExpireArray<T> extends Array<T> {
  private timeout: number;
  private times: number[] = [];
  private nextGC: NodeJS.Timeout | undefined;
  private gcDelayMs: number;
  private onItemExpired: (expiredElement: T) => void;

  constructor(timeout: number, gcDelayMs: number, onItemExpired: (expiredElement: T) => void) {
    super();
    if (!Number.isFinite(timeout)) {
      throw new Error('Timeout must be a number');
    }
    this.timeout = timeout;
    this.gcDelayMs = gcDelayMs;
    this.onItemExpired = onItemExpired;
  }

  scheduleGC(): void {
    if (this.nextGC) {
      return;
    }
    const ts = this.times.length ? this.times[0] - Date.now() + this.gcDelayMs : this.timeout;
    this.nextGC = setTimeout(this.gc.bind(this), ts);
    if (this.nextGC.unref) {
      this.nextGC.unref();
    }
  }

  gc(): void {
    const now = Date.now();
    while (this.times.length && this.times[0] < now) {
      this.times.shift();
      const removedValue = super.shift();
      if (removedValue !== undefined) {
        try {
          this.onItemExpired(removedValue);
        } catch (e) {
          // Don't let an exception in the callback prevent us from scheduling the next GC.
          if (typeof e === 'string') {
            logger.error(e);
          }
        }
      }
    }
    this.nextGC = undefined;
    this.scheduleGC();
  }

  push(...items: T[]): number {
    if (!this.times.length) {
      this.scheduleGC();
    }
    const time = Date.now() + this.timeout;
    items.forEach(() => {
      this.times.push(time);
    });
    return super.push(...items);
  }

  pop(): T | undefined {
    if (!this.times.length && this.nextGC) {
      clearTimeout(this.nextGC);
      this.nextGC = undefined;
    }
    this.times.pop();
    return super.pop();
  }

  shift(): T | undefined {
    if (this.nextGC) {
      clearTimeout(this.nextGC);
      this.nextGC = undefined;
    }
    this.scheduleGC();
    this.times.shift();
    return super.shift();
  }
}
